import { Component, Input, OnChanges, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import * as moment from 'moment';
import { LoaderService } from 'src/app/loader.service';
import { AlertDialogService } from 'src/app/shared/alert-dialog/alert-dialog.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import Utils from 'src/app/utils/utils';
import { SubscriptionService } from '../../subscription.service';
import { subscriptionCreateSuccessAlert, subscriptionOperationFailureAlert } from '../../subscription.create-alert';

export interface TrialSubsContext {
  city: { id: string, name: string };
  mode: { id: string, name: string };
  category: { id: string, name: string };
  service: { id: string, name: string, serviceDetailId: string, active?: boolean };
  modeServices: { id: string, name: string, serviceDetailId: string, active?: boolean }[];
}

@Component({
  selector: 'app-create-trial-subs',
  templateUrl: './trial.create.component.html',
  styleUrls: ['./trial.create.component.css']
})
export class CreateTrialSubsComponent implements OnInit, OnChanges {
  @Input() context: TrialSubsContext = null;

  public formGroup = new FormGroup({
    ruleName: new FormControl(null),
    city: new FormControl(null),
    services: new FormControl([]),
    priority: new FormControl(null),
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    ruleType: new FormControl({ value: 'zeroCommission', disabled: true }),
    maxRides: new FormControl(null),
    periodInDays: new FormControl(null),
    suspendServicesPostExpiry: new FormControl(true)
  });

  public services: { id: string, name: string, serviceDetailId: string, mode?: string, active?: boolean, label?: string }[] = [];
  public today: Date = moment().startOf('hour').toDate();
  public tomorrowEod: Date = moment().endOf('day').minutes(30).add(1, 'days').toDate();
  public maxStartDate: Date;
  public minEndDate: Date = this.tomorrowEod;
  public Utils = Utils;
  @ViewChild('OverlappingPriorityRuleAlert') OverlappingPriorityRuleAlert: TemplateRef<any>;
  public overlappingRules: any = [];
  private lastServiceKey = '';

  constructor(
    private subsService: SubscriptionService,
    private loader: LoaderService,
    private alertService: AlertDialogService,
    public toasterService: ToasterService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.applyContext();
  }

  ngOnChanges() {
    this.applyContext();
  }

  formValue(name: string) {
    return this.formGroup.get(name).value;
  }

  blockInvalidNumberKeys(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  rejectNonPositiveAmount(event: Event, controlName: string) {
    const input = event.target as HTMLInputElement;
    if (input.value === '' || input.value == null) {
      return;
    }
    if (Number(input.value) <= 0) {
      this.formGroup.get(controlName).setValue(null);
      input.value = '';
    }
  }

  changeInDate() {
    const startDate = this.formValue('startDate');
    if (startDate) {
      this.minEndDate = startDate;
    }
    const endDate = this.formValue('endDate');
    if (endDate) {
      this.maxStartDate = endDate;
    }
  }

  errorMessage(message: string) {
    this.toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message
    }));
  }

  validateField(fieldName: string, message: string) {
    const value = this.formValue(fieldName);
    if (!value) {
      this.errorMessage(message);
      throw new Error(message);
    }
    return value;
  }

  async createTrialRule() {
    this.loader.openLoading();
    try {
      const ruleName = this.validateField('ruleName', 'Rule name is required');
      const city = this.validateField('city', 'City is required');
      const selectedIds = this.formValue('services') || [];
      if (!selectedIds.length) {
        this.errorMessage('Services is required');
        throw new Error('Services is required');
      }
      const byId = {};
      this.services.forEach(service => {
        byId[service.id] = service;
      });
      const services = selectedIds.map(id => byId[id]).filter(Boolean);
      if (!services.length) {
        this.errorMessage('Services is required');
        throw new Error('Services is required');
      }
      const startTime = moment(this.validateField('startDate', 'Purchase Start date is required').getTime()).unix();
      const endTime = moment(this.validateField('endDate', 'Purchase End date is required').getTime()).unix();
      const priority = this.validateField('priority', 'Priority is required');
      const maxRides = this.validateField('maxRides', 'Max rides is required');
      const periodInDays = this.validateField('periodInDays', 'Period in days is required');
      if (!Number.isInteger(Number(periodInDays)) || Number(periodInDays) <= 0) {
        const message = 'Period in days must be a positive whole number';
        this.errorMessage(message);
        throw new Error(message);
      }
      const serviceDetailIds = services.map(s => s.serviceDetailId);

      const priorityNum = Number(priority);
      try {
        const existing: any = await this.subsService
          .getSamePriorityRules(serviceDetailIds, startTime, endTime, [priorityNum])
          .toPromise();
        if (existing && existing.length > 0) {
          this.overlappingRules = existing;
          this.dialog.open(this.OverlappingPriorityRuleAlert);
          return;
        }
      } catch (overlapError) {
        console.warn('Could not verify overlapping rules', overlapError);
        this.alertService.open({
          title: 'Error',
          message: 'Could not verify overlapping rules. Please try again.'
        });
        return;
      }

      const payload = {
        type: 'TRIAL',
        rules: [{
          ruleName,
          startTime,
          endTime,
          priority: priorityNum,
          city: city.id,
          services: services.map(s => s.id),
          serviceDetails: serviceDetailIds,
          mode: this.context.mode.id,
          categoryLevelApplicability: {
            categories: [{
              id: this.context.category.id,
              allowedServices: [],
              excludedServices: []
            }]
          },
          isAutoAssignedToHH: false,
          isAutoAssignedToMC: false,
          isDefaultSubscription: false,
          exclusiveOfTax: false,
          userSelectors: [],
          userSelectorName: null,
          excludeIncentiveTypes: [],
          ruleType: 'zeroCommission',
          suspendServicesPostExpiry: true,
          rule: [{
            variable: 'commission',
            operator: '=',
            value: 0,
            type: 'flat'
          }],
          validity: [{ type: 'rides', maxValue: Number(maxRides) }],
          ruleAmount: 0,
          periodInDays: Number(periodInDays),
          isRecommended: false
        }]
      };

      const resp: any = await this.subsService.createSubscriptionRules(payload).toPromise();
      this.alertService.open(subscriptionCreateSuccessAlert(resp, { configRequestKind: 'subscription' }));
    } catch (error) {
      this.alertService.open(subscriptionOperationFailureAlert(error));
    } finally {
      this.loader.closeLoading();
    }
  }

  private applyContext() {
    if (!this.context) {
      return;
    }
    this.formGroup.get('city').setValue(this.context.city, { emitEvent: false });
    const modeId = this.context.mode && this.context.mode.id;
    const nextServices = (this.context.modeServices || []).map(service => Object.assign({}, service, {
      mode: modeId,
      label: service.name + (service.active === false ? ' (inactive)' : '')
    }));
    const categoryId = this.context.category && this.context.category.id;
    const selectedId = this.context.service && this.context.service.id;
    const key = (this.context.mode && this.context.mode.id) + ':' + categoryId + ':' + selectedId + ':' + nextServices.map(service => service.id).join(',');
    this.services = nextServices;
    if (key !== this.lastServiceKey) {
      this.lastServiceKey = key;
      this.formGroup.get('services').setValue(selectedId ? [selectedId] : [], { emitEvent: false });
    }
    const suspend = this.formGroup.get('suspendServicesPostExpiry');
    suspend.setValue(true, { emitEvent: false });
    suspend.disable({ emitEvent: false });
  }
}
